<script setup lang="ts">
import { BriefcaseIcon } from '@heroicons/vue/24/outline'
import { highlight } from '@/utils/highlight'
import type { OsOpportunityResult } from '@/api/omnisearchApi'

const props = defineProps<{
  item: OsOpportunityResult
  query: string
}>()

const emit = defineEmits<{ navigate: [] }>()

function getHref(): string | null {
  // Fiche client 360° directement sur l'onglet Affaires (route client-360 = /client/:companyUuid/:tab?).
  if (props.item.companyUuid) return `/client/${props.item.companyUuid}/affaires`
  // Repli : ancienne fiche partner legacy (id Odoo) si la company boost-suite n'est pas résolue.
  if (props.item.partnerId) return `/partner/${props.item.partnerId}`
  return null
}

function formatRevenue(value: number): string {
  return value.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
}

function formatDeadline(raw: string | null): string {
  if (!raw) return ''
  const d = new Date(raw)
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR')
}

function handleClick() {
  const href = getHref()
  if (href) {
    emit('navigate')
    window.location.href = href
  }
}
</script>

<template>
  <button
    class="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-base-200 transition-colors text-left group"
    :class="getHref() ? 'cursor-pointer' : 'cursor-default opacity-70'"
    @click="handleClick"
  >
    <span class="mt-0.5 flex-shrink-0 w-8 h-8 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
      <BriefcaseIcon class="w-4 h-4 text-amber-600 dark:text-amber-400" />
    </span>
    <div class="flex-1 min-w-0">
      <p
        class="text-sm font-medium truncate text-base-content"
        v-html="highlight(item.name || '—', query)"
      />
      <div class="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
        <span
          v-if="item.partnerName"
          class="text-xs text-base-content/60"
          v-html="highlight(item.partnerName, query)"
        />
        <span
          v-if="item.partnerSiret"
          class="text-xs text-base-content/50"
          v-html="'SIRET ' + highlight(item.partnerSiret, query)"
        />
        <span v-if="item.expectedRevenue" class="text-xs text-base-content/50">
          {{ formatRevenue(item.expectedRevenue) }}
        </span>
        <span v-if="formatDeadline(item.dateDeadline)" class="text-xs text-base-content/40">
          {{ formatDeadline(item.dateDeadline) }}
        </span>
      </div>
    </div>
    <span
      v-if="item.stageName"
      class="badge badge-sm badge-ghost self-center flex-shrink-0"
    >
      {{ item.stageName }}
    </span>
    <span v-else-if="!getHref()" class="text-xs text-base-content/40 self-center flex-shrink-0">pas de lien</span>
    <span v-else class="text-xs text-base-content/40 self-center flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
  </button>
</template>
